import type { AiState } from './types'
import { REQUIRED_MSG, statusOptions } from './constants'
import { FieldCell, TwoColRow } from './styles'
import WizrInput from '../../WizrInput'
import WizrDropDown from '../../WizrDropDown'

interface AiCoreFieldsProps {
  aiState: AiState
  setAiField: (key: keyof AiState, value: string) => void
  aiErrors: Partial<Record<string, boolean>>
  clearAiErrorKey: (key: string) => void
  existingRecordId: string | undefined
}

const inputStyle = { width: '100%', marginTop: '0' } as const
const inputBg = '#F2F4F9' as const

const LlmDbCoreFields = ({
  aiState,
  setAiField,
  aiErrors,
  clearAiErrorKey,
  existingRecordId,
}: AiCoreFieldsProps): JSX.Element => (
  <>
    <TwoColRow
      left={
        <FieldCell label='Display Name' fieldKey='displayName'>
          <WizrInput
            style={inputStyle}
            border='1px solid #000'
            background={inputBg}
            placeholder='Enter Name'
            value={aiState.displayName}
            onChange={(v: string) => {
              setAiField('displayName', v)
              if (v?.trim()) clearAiErrorKey('displayName')
            }}
            showError={!!aiErrors.displayName}
            errorMessage={aiErrors.displayName ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='System Version' fieldKey='systemVersion'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter System Version'
            value={aiState.systemVersion}
            onChange={(v: string) => {
              setAiField('systemVersion', v)
              if (v?.trim()) clearAiErrorKey('systemVersion')
            }}
            showError={!!aiErrors.systemVersion}
            errorMessage={aiErrors.systemVersion ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='Status' fieldKey='status'>
          <WizrDropDown
            placeHolder='Select Status'
            options={statusOptions}
            value={aiState.status || undefined}
            onChange={(v: string) => {
              setAiField('status', v)
              if (v) clearAiErrorKey('status')
            }}
            style={{ marginTop: '5px', width: '100%' }}
            showError={!!aiErrors.status}
            errorMessage={aiErrors.status ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Model Type' fieldKey='modelType'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Type'
            value={aiState.modelType}
            onChange={(v: string) => {
              setAiField('modelType', v)
              if (v?.trim()) clearAiErrorKey('modelType')
            }}
            showError={!!aiErrors.modelType}
            errorMessage={aiErrors.modelType ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='LLM Type' fieldKey='llmType'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter LLM Type'
            value={aiState.llmType}
            onChange={(v: string) => {
              setAiField('llmType', v)
              if (v?.trim()) clearAiErrorKey('llmType')
            }}
            showError={!!aiErrors.llmType}
            errorMessage={aiErrors.llmType ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Deployment Name' fieldKey='deploymentName'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Deployment Name'
            value={aiState.deploymentName}
            onChange={(v: string) => {
              setAiField('deploymentName', v)
              if (v?.trim()) clearAiErrorKey('deploymentName')
            }}
            showError={!!aiErrors.deploymentName}
            errorMessage={aiErrors.deploymentName ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='Model Name' fieldKey='modelName'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Model Name'
            value={aiState.modelName}
            onChange={(v: string) => {
              setAiField('modelName', v)
              if (v?.trim()) clearAiErrorKey('modelName')
            }}
            showError={!!aiErrors.modelName}
            errorMessage={aiErrors.modelName ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='API Type' fieldKey='apiType'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter API Type'
            value={aiState.apiType}
            onChange={(v: string) => {
              setAiField('apiType', v)
              if (v?.trim()) clearAiErrorKey('apiType')
            }}
            showError={!!aiErrors.apiType}
            errorMessage={aiErrors.apiType ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='API Base URL' fieldKey='apiBaseUrl'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter API Base URL'
            value={aiState.apiBaseUrl}
            onChange={(v: string) => {
              setAiField('apiBaseUrl', v)
              if (v?.trim()) clearAiErrorKey('apiBaseUrl')
            }}
            showError={!!aiErrors.apiBaseUrl}
            errorMessage={aiErrors.apiBaseUrl ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='API Version' fieldKey='apiVersion'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter API Version'
            value={aiState.apiVersion}
            onChange={(v: string) => {
              setAiField('apiVersion', v)
              if (v?.trim()) clearAiErrorKey('apiVersion')
            }}
            showError={!!aiErrors.apiVersion}
            errorMessage={aiErrors.apiVersion ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='API Secret Key' fieldKey='apiSecretKey'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Select API Secret Key'
            value={aiState.apiSecretKey}
            onChange={(v: string) => {
              setAiField('apiSecretKey', v)
              if (v?.trim()) clearAiErrorKey('apiSecretKey')
            }}
            showError={!!aiErrors.apiSecretKey}
            errorMessage={aiErrors.apiSecretKey ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='API Secret Value' fieldKey='apiSecretValue'>
          <WizrInput
            type='password'
            style={inputStyle}
            background={inputBg}
            placeholder='Enter API Value'
            value={aiState.apiSecretValue}
            onChange={(v: string) => {
              setAiField('apiSecretValue', v)
              if (v?.trim()) clearAiErrorKey('apiSecretValue')
            }}
            showError={!!aiErrors.apiSecretValue}
            errorMessage={
              !existingRecordId && aiErrors.apiSecretValue ? REQUIRED_MSG : undefined
            }
          />
        </FieldCell>
      }
    />
  </>
)

export default LlmDbCoreFields
